/**
 * Give an uploaded file a name people can recognise: "scan_0042.pdf" becomes
 * "Carta identità Rossi.pdf". Only the name is edited; the extension is shown beside
 * the box and kept by the server, so the file still opens in the right program.
 */

import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { Field } from '@/components/ui';
import { useI18n } from '@/i18n';
import { ApiError } from '@/lib/api';

/** "nomina.pdf.p7m" → ["nomina.pdf", ".p7m"]: the server keeps the last extension. */
function split(filename: string): [string, string] {
  const dot = filename.lastIndexOf('.');
  return dot > 0 && dot < filename.length - 1 ? [filename.slice(0, dot), filename.slice(dot)] : [filename, ''];
}

export function RenameFileDialog({
  filename,
  onClose,
  onSave,
}: {
  /** The current name; null closes the dialog. */
  filename: string | null;
  onClose: () => void;
  onSave: (name: string) => Promise<unknown>;
}) {
  const { t } = useI18n();
  const [base, ext] = split(filename ?? '');
  const [name, setName] = useState(base);

  useEffect(() => {
    if (filename !== null) setName(split(filename)[0]);
  }, [filename]);

  const save = useMutation({
    mutationFn: () => onSave(`${name.trim()}${ext}`),
    onSuccess: onClose,
  });

  const unchanged = name.trim() === base;

  return (
    <Modal
      open={filename !== null}
      onClose={onClose}
      size="sm"
      title={t('rename.title')}
      footer={
        <>
          <button
            type="button"
            className="btn-primary"
            disabled={!name.trim() || unchanged || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('common.saving') : t('common.save')}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() && !unchanged) save.mutate();
        }}
      >
        <Field label={t('rename.name')} hint={ext ? t('rename.extensionKept', { ext }) : undefined}>
          <div className="flex items-center gap-2">
            <input
              className="input"
              autoFocus
              maxLength={180}
              value={name}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setName(e.target.value)}
            />
            {ext ? <span className="text-sm text-muted whitespace-nowrap">{ext}</span> : null}
          </div>
        </Field>
        {save.error ? (
          <p role="alert" className="text-sm text-danger">
            {save.error instanceof ApiError ? save.error.message : t('common.saveError')}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
